import React, { useState, useContext } from 'react'

//Node Modules
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {Navbar, NavDropdown, Nav} from 'react-bootstrap'

//Components
import AboutModal from 'components/AboutModal'
import Divider from 'components/Divider'
import { SearchIcon, MenuIcon, OverflowIcon, CloseIcon} from 'components/SASIcon'
import SettingsModal from 'components/SettingsModal'
import LanguageSelector from 'components/LanguageSelector'

//Contexts
import { ParentSortContext } from 'context/content-context'

//Styles
import styles from './MainNav.module.scss'

//Util
import {checkParameterExistsByName} from 'util/url'
import { WELCOME_PAGE_CSS } from 'util/constants'

//Types
import { DocsetInfo } from 'types/docset'

//Hooks
import { useIsSmallDevice } from 'hooks'

export interface Props {
  title?: string
  linkTitle?: string
  defaultLink?: string
  version?: string
  versionPicker?: React.ReactNode
  pdf?: string
  info?: DocsetInfo
  hideSidebarToggle?: boolean
  onSidebarToggleClick?: () => void
  sidebarVisible: boolean
  onSearchClick?: () => void
  onPDFDownloadClick?: () => void
  minimal?: boolean
}

const MainNav = ({title, linkTitle, defaultLink, version, versionPicker, pdf, info, hideSidebarToggle, onSidebarToggleClick, sidebarVisible, onSearchClick, minimal, onPDFDownloadClick}:Props) => {
  const [showAbout, setShowAbout] = useState<boolean>(false)
  const [showSettings, setShowSettings] = useState<boolean>(false)
  const [helpOpen, setHelpOpen] = useState<boolean>(false)
  const parentSortContext = useContext(ParentSortContext)
  const { t } = useTranslation()

  // Set up the email template
  const subject = document.title

  const isSmallDevice = useIsSmallDevice()

  const url = encodeURIComponent(window.location.href)
  const feedbackText = t('helpcenter-gui-icu.feedback.email.body.feedback.label')
  const pageText = t('helpcenter-gui-icu.feedback.email.body.page.label', { url })
  const emailMailto = `mailto:?subject=${subject}&body=${url}`
  const feedbackMailto = `mailto:yourturn@sas.com?subject=${subject}&body=${feedbackText}%0D%0A%0D%0A${pageText}`
  const titleElem = <Nav.Item className={styles.title}>{linkTitle ? linkTitle: ''}{linkTitle && title ? ' / ':''} {title ? title : ''}</Nav.Item>
  const breadcrumbLink = linkTitle ? defaultLink ? <Link to={defaultLink} className={`${styles.ellipsis} ${styles.breadcrumbLink}`} onClick={handleBreadcrumbLink}>{linkTitle} </Link>: <span className={styles.ellipsis}>{linkTitle} </span> : ''
  const mobileTitle = <>{breadcrumbLink} {linkTitle && title ? `/`:''} {title ? <span className={`${styles.ellipsis} ${styles.breadcrumbRight}`}>{title}</span> : ''} {<span>version</span> ? <><span className={styles.vertDivider}>|</span> <span>{version}</span></> : ''} </>
  
  const navLink = isSmallDevice ? '' : 'navLink'

  let hidePdf = false
  let stylesheets = document.styleSheets

  Array.from(stylesheets).forEach(stylesheet => {
    let node = stylesheet?.ownerNode as Element
    let id = node?.id
    if (id === WELCOME_PAGE_CSS) hidePdf = true
  })
  
  const pdfLink = (type:string) => {
    if (parentSortContext.parentSort) {
      pdf = undefined
    }
    return <Link 
      onClick={onPDFDownloadClick} 
      tabIndex={pdf === undefined  ? -1 : 0} 
      className={`${type} ${pdf === undefined  ? 'disabled' : ''} ${navLink}`}  
      to={pdf || ''} 
      target="_blank" 
      rel="noopener canonical">
      {t('helpcenter-gui-icu.mainnav.download.pdf.label')}
    </Link>
  }

  const feedbackLink = <Nav.Link className={`${styles.feedbackLink}`} href={feedbackMailto} active={false} title={t('helpcenter-gui-icu.mainnav.feedback.title')}>{t('helpcenter-gui-icu.mainnav.feedback.label')}</Nav.Link>

  function handleBreadcrumbLink() {
    document.dispatchEvent(new MouseEvent('click'));
  }

  function helpToggle(isOpen:boolean) {
    setHelpOpen(isOpen)
  }

  const navLinks = !minimal ?
    <Divider variant="dark">
      { !hidePdf && pdfLink('nav-link') }
      { feedbackLink }
      {<LanguageSelector isHelp={checkParameterExistsByName('help')}/> }
    </Divider> :
    <Divider variant="dark">
      { feedbackLink }
    </Divider>
  
  return (
    <>
      <Navbar className={`${styles.mainnav} ${isSmallDevice ? styles.isSmallDevice : ''}`} variant="dark" role="none">
	      { !hideSidebarToggle && <Nav.Link onClick={onSidebarToggleClick} bsPrefix={`${styles.tocToggle} ${styles.iconLink}`} aria-expanded={sidebarVisible ? true : false} aria-label={sidebarVisible ? t('helpcenter-gui-icu.mainnav.toc.hide.title') : t('helpcenter-gui-icu.mainnav.toc.show.title')} title={sidebarVisible ? t('helpcenter-gui-icu.mainnav.toc.hide.title') : t('helpcenter-gui-icu.mainnav.toc.show.title')}>{isSmallDevice && sidebarVisible ? <CloseIcon /> : <MenuIcon />}</Nav.Link> }
        <Nav className={styles.leftnav}>
          
          { 
            isSmallDevice ?
              <>
                <span className={styles.sasMobileText}>{t('helpcenter-gui-icu.header.banner.title')}</span>
              </> : <div className={styles.titleElem}>{titleElem}</div>
          }
          {
            versionPicker && !isSmallDevice ? versionPicker : null
          }
        </Nav>
        <Nav className={styles.rightnav}>
          { !minimal && <Nav.Link bsPrefix={`${styles.iconLink} ${styles.searchIcon}`} onClick={onSearchClick} aria-label={t('helpcenter-gui-icu.mainnav.search_button.aria')} title={t('helpcenter-gui-icu.mainnav.search.title')}><SearchIcon /></Nav.Link> }
          {!isSmallDevice && <span className={styles.vertDivider}>|</span>}
          { !isSmallDevice && navLinks }
          { (isSmallDevice ) &&
            <LanguageSelector isHelp={checkParameterExistsByName('help')}/> 
          }
          { (isSmallDevice || !minimal) &&
            <NavDropdown id="mainnav-help-menu-dropdown"
              alignRight
              className={styles.iconLink}
              onToggle={helpToggle}
              title={<div className="overflowIconContainer" aria-label={t('helpcenter-gui-icu.mainnav.more_dropdown_button.aria')} data-testid="overflow-icon" title={t('helpcenter-gui-icu.mainnav.more.title')}>{helpOpen ? <CloseIcon /> : <OverflowIcon />}</div>}
            >
              <div className={styles.dropdownMenu} role="application">
                {isSmallDevice && !minimal ? <div className={`${styles.dropdownItem} ${styles.breadcrumb}`}>{mobileTitle}</div> : null}
                <div className={styles.menuItemsContainer}>
                  {isSmallDevice ? 
                    <>
                      { !minimal && !hidePdf && pdfLink(`dropdown-item ${styles.dropdownItem}`) }
                      <NavDropdown.Item className={styles.dropdownItem} href={feedbackMailto} title={t('helpcenter-gui-icu.mainnav.feedback.title')}>{t('helpcenter-gui-icu.mainnav.feedback.label')}</NavDropdown.Item > 
                    </>: null}
                  { !minimal && 
                    <>
                      <div className={styles.hDivider}></div>
                      <NavDropdown.Item className={styles.dropdownItem} to={`helptips.htm?help`} as={Link}>{t('helpcenter-gui-icu.mainnav.help.menu.help_tips.label')}</NavDropdown.Item>
                      <NavDropdown.Item className={styles.dropdownItem} to={`accessibility.htm?help`} as={Link}>{t('helpcenter-gui-icu.mainnav.help.menu.accessibility.label')}</NavDropdown.Item>
                      <div className={styles.hDivider}></div>
                      <NavDropdown.Item className={styles.dropdownItem} href={emailMailto} active={false}>{t('helpcenter-gui-icu.mainnav.help.menu.email.label')}</NavDropdown.Item>
                      <NavDropdown.Item className={styles.dropdownItem} onClick={() => setShowSettings(true)} data-testid="about-modal">{t('helpcenter-gui-icu.mainnav.help.menu.settings.label')}</NavDropdown.Item>
                      <NavDropdown.Item className={styles.dropdownItem} onClick={() => setShowAbout(true)} data-testid="settings-modal">{t('helpcenter-gui-icu.mainnav.help.menu.about.label')}</NavDropdown.Item>
                    </>
                  }
                  <div className={styles.hDivider}></div>
                  { isSmallDevice ? 
                    <>
                      <NavDropdown.Item className={styles.dropdownItem} href="https://support.sas.com/en/support-home.html">
                        {t('helpcenter-gui-icu.header.customer_support.link.title')}
                      </NavDropdown.Item>
                      <NavDropdown.Item className={styles.dropdownItem} href="https://support.sas.com/en/documentation.html">
                        {t('helpcenter-gui-icu.header.support_home.link.title')}
                      </NavDropdown.Item>
                    </> : null}
                </div>
              </div>
            </NavDropdown>
          }
        </Nav>
      </Navbar>

      { info && <AboutModal info={info} show={showAbout} onHide={() => setShowAbout(false)} /> }
      <SettingsModal show={showSettings} onHide={() => setShowSettings(false)} />
    </>
  )
}

export default MainNav